import React, { Suspense, useState } from 'react';

import { SettingsPageFrame, TableSkeleton } from '@eluve/blocks';
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import {
  useIsEluveAdmin,
  useIsPromptTemplateCreator,
} from '@eluve/session-helpers';

import { CreateTemplateBundleWizard } from './CreateTemplateBundleWizard';
import { TenantTemplateBundlesList } from './TenantTemplateBundlesList';

export interface TenantTemplateBundlesListPageProps {}

/**
 * The new template bundle management experience for an organization,
 * replacing the legacy output templates list (ENG-4547). Shown when the
 * TEMPLATE_BUNDLE_MANAGEMENT feature flag is enabled for the tenant.
 */
export const TenantTemplateBundlesListPage: React.FC<
  TenantTemplateBundlesListPageProps
> = () => {
  useCaptureEventOnMount('page_view_template_bundles_list');
  const isTemplateCreator = useIsPromptTemplateCreator();
  const isEluveAdmin = useIsEluveAdmin();

  const isCreateTemplateDisabled = !(isEluveAdmin || isTemplateCreator);

  const [isWizardOpen, setIsWizardOpen] = useState(false);

  return (
    <>
      <SettingsPageFrame
        size="wide"
        title="Templates"
        actions={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                disabled={isCreateTemplateDisabled}
                onClick={() => setIsWizardOpen(true)}
              >
                <Icon name="Plus" size="auto" />
                Create Template
              </Button>
            </TooltipTrigger>
            {isCreateTemplateDisabled && (
              <TooltipContent>
                You do not have permission to create templates. Please contact
                your organization's administrator.
              </TooltipContent>
            )}
          </Tooltip>
        }
      >
        <Suspense fallback={<TableSkeleton rowCount={20} />}>
          <TenantTemplateBundlesList />
        </Suspense>
      </SettingsPageFrame>
      <CreateTemplateBundleWizard
        open={isWizardOpen}
        onOpenChange={setIsWizardOpen}
      />
    </>
  );
};
